/**
 * 注册弹窗主控制器（五步制：必读 → 信息+验证 → 检测 → 密码 → 完成）
 * - 信息步提交时自动触发三要素验证（内联回显），邮箱验证码并行完成
 * - 检测步自动执行身份检测并分流（登录引导/合并/绑定/释放/换号短信/新用户）
 * - 密码步提交注册成功后直接注入双令牌（注册即登录，免激活）
 *
 * 本文件保留全部状态编排、校验调度与提交流程；UI 子区块拆分至 components/，
 * 流程细节拆分至 hooks/（useReleaseFlow / useDebouncedChecks / useVerificationFlow / useModalActions）。
 */
import { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useRegisterStore } from '../../../../stores/registerStore';
import type { RegisterErrorFlags } from '../../../../stores/registerStore';
import { useAuthStore } from '../../../../stores/authStore';

import { Step1RegisterGuide } from '../RegisterSteps/Step1RegisterGuide';
import { Step2InfoVerify } from '../RegisterSteps/Step2InfoVerify';
import { Step3SystemCheck } from '../RegisterSteps/Step3SystemCheck';
import { Step4Password } from '../RegisterSteps/Step4Password';
import { Step5Success } from '../RegisterSteps/Step5Success';
import { ManualApplicationModal, IdentityMergeModal, CustomerBindingModal } from '../RegisterSteps/modals';
import { MismatchResolutionModal } from '../RegisterSteps/MismatchResolutionModal';
import { maskPhone } from '../RegisterSteps/mask';
import { ModalToast } from './components/ModalToast';
import { ModalHeader } from './components/ModalHeader';
import { StepIndicator } from './components/StepIndicator';
import { ModalFooter } from './components/ModalFooter';
import { useReleaseFlow } from './hooks/useReleaseFlow';
import { useDebouncedChecks } from './hooks/useDebouncedChecks';
import { useVerificationFlow } from './hooks/useVerificationFlow';
import { useModalActions } from './hooks/useModalActions';
import { useSiteConfig } from '../../../../hooks/useSiteConfig';
import type { RegisterModalProps, ToastType } from './types';

export default function RegisterModal({ isOpen, onClose }: RegisterModalProps) {
  const navigate = useNavigate();

  // 提示信息状态
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<ToastType>('info');
  const [submitting, setSubmitting] = useState(false);

  // 三要素核验连续业务失败计数（网络错误不计入；达到阈值引导人工审核）
  const verifyFailCountRef = useRef(0);

  // 人工审核申请来源（mismatch = 认领「非本人信息」流程进入；取消填写时需返回来源弹窗）
  const manualReviewSourceRef = useRef<'normal' | 'mismatch'>('normal');

  // 认领弹窗「非本人信息」申诉凭证：验证通过仅持有（不动库），注册提交时后端才腾位/停用
  const [disputeToken, setDisputeToken] = useState<string | null>(null);

  // 从store获取状态和动作
  const {
    currentStep,
    formData,
    errors,
    agreed,
    showPassword,
    showConfirmPassword,
    registerCountdown,
    hasReadToBottom,
    usernameStatus,
    emailStatus,
    phoneStatus,
    backupPhoneStatus,
    idCardStatus,
    passwordStrength,
    identityVerification,
    emailCodeCountdown,
    emailCodeSending,
    emailCodeSentTo,
    smsCodeCountdown,
    smsCodeSending,
    systemCheckLoading,
    systemCheckResult,

    registerSuccessData,
    showManualApplication,
    manualApplicationReason,
    manualApplicationPhoto,
    showIdentityMerge,
    selectedPrimaryIdentity,
    showCustomerBinding,
    showMismatchResolution,

    setStep,
    nextStep,
    prevStep,
    setFormField,
    setError,

    resetForm,
    setAgreed,
    setShowPassword,
    setShowConfirmPassword,
    decrementCountdown,
    setRegisterCountdown,
    setHasReadToBottom,
    setUsernameStatus,
    calculatePasswordStrength,
    sendEmailCode,
    decrementEmailCodeCountdown,
    sendUpgradeSmsCode,
    decrementSmsCodeCountdown,
    resetIdentityVerification,
    submitRegister,
    setShowManualApplication,
    setManualApplicationReason,
    setManualApplicationPhoto,
    setShowIdentityMerge,
    setSelectedPrimaryIdentity,
    setShowCustomerBinding,
    setShowMismatchResolution,
    validateField,
    validateStep2,
    validateStep4,
    resetStore
  } = useRegisterStore();

  const applyTokenPair = useAuthStore((state) => state.applyTokenPair);
  const signOut = useAuthStore((state) => state.signOut);

  // Toast提示
  const showToast = (message: string, type: ToastType = 'info') => {
    setToastMessage(message);
    setToastType(type);
    // 3秒后自动清除提示
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  // 规则 4：老手机号释放 —— 短信验证流程状态（本地管理，不入 store）
  const {
    releaseFlow,
    setReleaseFlow,
    resetReleaseFlow,
    releaseCountdown,
    releaseSending,
    handleSendReleaseCode,
    handleVerifyReleaseCode,
  } = useReleaseFlow(formData, showToast);

  // 信息步字段唯一性 blur 检查（带防抖）
  const {
    handleUsernameBlur,
    handleEmailBlur,
    handlePhoneBlur,
    handleBackupPhoneBlur,
    handleIdCardBlur,
  } = useDebouncedChecks(showToast);

  // 实名三要素验证 + 系统检测流程
  const {
    handleIdentityVerification,
    handleSystemCheck,
  } = useVerificationFlow(formData, showToast, verifyFailCountRef, setReleaseFlow);

  // 关闭弹窗时重置状态
  const handleClose = useCallback(() => {
    resetStore();
    resetForm();
    resetReleaseFlow();
    setDisputeToken(null);
    verifyFailCountRef.current = 0;
    onClose();
  }, [resetStore, resetForm, resetReleaseFlow, onClose]);

  // 强制阅读倒计时取后台配置（「条款与协议 → 强制阅读倒计时（秒）」，0 = 无需等待）。
  // resetStore 会把 registerCountdown 重置回兜底值，因此每次打开弹窗时按配置重新初始化
  const { termsCountdownSeconds } = useSiteConfig();
  useEffect(() => {
    if (isOpen) setRegisterCountdown(termsCountdownSeconds);
  }, [isOpen, termsCountdownSeconds, setRegisterCountdown]);

  // 倒计时效果（必读条款）
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (currentStep === 1 && registerCountdown > 0) {
      timer = setInterval(() => {
        decrementCountdown();
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [currentStep, registerCountdown, decrementCountdown]);

  // 邮箱验证码 60s 重发倒计时
  useEffect(() => {
    if (emailCodeCountdown <= 0) return;
    const timer = setInterval(() => {
      decrementEmailCodeCountdown();
    }, 1000);
    return () => clearInterval(timer);
  }, [emailCodeCountdown, decrementEmailCodeCountdown]);

  // 升格换号短信 60s 重发倒计时
  useEffect(() => {
    if (smsCodeCountdown <= 0) return;
    const timer = setInterval(() => {
      decrementSmsCodeCountdown();
    }, 1000);
    return () => clearInterval(timer);
  }, [smsCodeCountdown, decrementSmsCodeCountdown]);

  // 密码强度计算
  useEffect(() => {
    calculatePasswordStrength(formData.password);
  }, [formData.password, calculatePasswordStrength]);

  // 当进入检测步时自动触发系统检测（经 ref 调用避免每轮渲染重复触发）
  const handleSystemCheckRef = useRef(handleSystemCheck);
  handleSystemCheckRef.current = handleSystemCheck;
  useEffect(() => {
    if (currentStep === 3) {
      handleSystemCheckRef.current();
    }
  }, [currentStep]);

  // 处理下一步
  const handleNext = async () => {
    // 步骤1：注册必读
    if (currentStep === 1) {
      if (!agreed) {
        showToast('请阅读并同意注册协议', 'info');
        return;
      }
      if (!hasReadToBottom) {
        showToast('请阅读到内容末尾', 'info');
        return;
      }
      if (registerCountdown > 0) {
        showToast('请等待倒计时结束', 'info');
        return;
      }
      nextStep();
      return;
    }

    // 步骤2：信息填写 + 双重验证（提交时自动触发三要素验证）
    if (currentStep === 2) {
      if (!validateStep2()) {
        showToast('请检查并完善个人信息', 'info');
        return;
      }
      if (usernameStatus === 'taken' || emailStatus === 'taken' || phoneStatus === 'taken' || backupPhoneStatus === 'taken' || idCardStatus === 'taken') {
        showToast('您已注册，请直接登录', 'info');
        return;
      }
      await handleIdentityVerification();
      return;
    }

    // 步骤4：提交注册
    if (currentStep === 4) {
      await handleSubmitRegister();
      return;
    }
  };

  // 发送邮箱验证码
  const handleSendEmailCode = async () => {
    try {
      await sendEmailCode();
      showToast('验证码已发送，请查收邮箱（5 分钟内有效）', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '发送失败，请稍后重试', 'error');
    }
  };

  // 发送升格换号短信验证码
  const handleSendUpgradeSmsCode = async () => {
    try {
      await sendUpgradeSmsCode();
      showToast('短信验证码已发送（5 分钟内有效）', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '发送失败，请稍后重试', 'error');
    }
  };

  // 提交注册（密码步；成功后注入双令牌 → 注册即登录）
  const handleSubmitRegister = async () => {
    if (!validateStep4()) {
      showToast('请检查并完善账号信息', 'info');
      return;
    }
    if (!agreed) {
      showToast('请阅读并同意用户协议', 'info');
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitRegister(releaseFlow.token, disputeToken ?? undefined);
      if (result.ok) {
        // 先进入第 5 步完成页，再注入双令牌建立会话——
        // 确保会话生效时 currentStep 已是 5，注册页的已登录跳转逻辑不会抢先触发
        nextStep();
        const data = useRegisterStore.getState().registerSuccessData;
        if (data?.tokens) {
          await applyTokenPair(data.tokens, {
            id: data.id,
            username: data.username,
            email: data.email ?? null,
            phone: data.phone ?? null,
            role: data.role ?? null,
            status: data.status ?? null,
            createdAt: null,
          });
        }
        showToast('注册成功，已自动登录！', 'success');
      } else {
        handleRegisterFailure(result.errorFlags, result.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 注册失败按后端标志路由
  const handleRegisterFailure = (flags: RegisterErrorFlags | undefined, message?: string) => {
    if (flags?.identityVerificationRequired) {
      setStep(2);
      resetIdentityVerification();
      showToast('实名核验记录已过期，请重新验证', 'error');
      return;
    }
    if (flags?.emailCodeInvalid) {
      setStep(2);
      setError('emailCode', '邮箱验证码错误或已过期，请重新输入');
      showToast(message || '邮箱验证码错误', 'error');
      return;
    }
    if (flags?.usernameTaken) {
      setStep(2);
      setUsernameStatus('taken');
      setError('username', '用户名已被占用，请更换用户名后重试');
      showToast('用户名已被占用', 'error');
      return;
    }
    if (flags?.alreadyRegistered) {
      showToast(message || '该身份已注册，请直接登录', 'info');
      return;
    }
    if (flags?.multipleIdentities) {
      setStep(3);
      showToast(message || '存在多个身份，请先完成合并', 'error');
      return;
    }
    if (flags?.requiresReview) {
      setManualApplicationReason(message || '注册信息与系统档案不一致，需要人工审核');
      setShowManualApplication(true);
      showToast(message || '需要人工审核', 'error');
      return;
    }
    if (flags?.smsVerificationRequired) {
      setStep(3);
      showToast(message || '请先完成新手机号短信验证', 'error');
      return;
    }
    showToast(message || '注册失败，请稍后重试', 'error');
  };

  // 辅助弹窗动作（人工申请/身份合并/客户绑定）
  const {
    handleSubmitManualApplication,
    handleMergeIdentities,
    handleMergeAppeal,
    handleConfirmCustomerBinding,
  } = useModalActions(formData, showToast, handleClose);

  if (!isOpen) return null;

  // 认领弹窗与「非本人信息」弹窗共用的命中档案实体
  const matchedCustomer =
    systemCheckResult?.matchedEntity ?? systemCheckResult?.matchedEntities?.[0] ?? null;
  const hasMatchedCustomer = !!(
    systemCheckResult?.matchedEntity || systemCheckResult?.matchedEntities?.length
  );

  // 检测步「继续注册」按钮可用性：
  // 仅 新用户（!exists）或 升格换号（phoneChanged）显示；已注册/多身份/复核/命中档案分支走各自引导
  const step3CanContinue =
    !!systemCheckResult &&
    !systemCheckResult.isRegistered &&
    !systemCheckResult.multipleIdentities &&
    !systemCheckResult.requiresReview &&
    !hasMatchedCustomer &&
    (!systemCheckResult.exists || !!systemCheckResult.phoneChanged);

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[600px] max-h-[90vh] mx-4 flex flex-col">
          {/* 提示信息 */}
          {toastMessage && (
            <ModalToast message={toastMessage} type={toastType} />
          )}

          {/* 顶部标题栏 */}
          <ModalHeader currentStep={currentStep} onClose={handleClose} />

          {/* 步骤指示器（五步） */}
          <StepIndicator currentStep={currentStep} />

          {/* 内容区 */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {currentStep === 1 && (
              <Step1RegisterGuide
                agreed={agreed}
                setAgreed={setAgreed}
                hasReadToBottom={hasReadToBottom}
                setHasReadToBottom={setHasReadToBottom}
                countdown={registerCountdown}
              />
            )}

            {currentStep === 2 && (
              <Step2InfoVerify
                formData={formData}
                errors={errors}
                setFormField={setFormField}
                validateField={validateField}
                usernameStatus={usernameStatus}
                emailStatus={emailStatus}
                phoneStatus={phoneStatus}
                backupPhoneStatus={backupPhoneStatus}
                idCardStatus={idCardStatus}
                identityVerification={identityVerification}
                emailCodeCountdown={emailCodeCountdown}
                emailCodeSending={emailCodeSending}
                emailCodeSentTo={emailCodeSentTo}
                onUsernameBlur={handleUsernameBlur}
                onEmailBlur={handleEmailBlur}
                onPhoneBlur={handlePhoneBlur}
                onBackupPhoneBlur={handleBackupPhoneBlur}
                onIdCardBlur={handleIdCardBlur}
                onSendEmailCode={handleSendEmailCode}
              />
            )}

            {currentStep === 3 && (
              <Step3SystemCheck
                loading={systemCheckLoading}
                result={systemCheckResult}
                onIdentityMerge={() => setShowIdentityMerge(true)}
                onCustomerBinding={() => setShowCustomerBinding(true)}
                onLogin={() => {
                  handleClose();
                  navigate('/login');
                }}
                releaseFlow={releaseFlow}
                releaseCountdown={releaseCountdown}
                releaseSending={releaseSending}
                onReleaseCodeChange={(code) =>
                  setReleaseFlow((prev) => ({ ...prev, code }))
                }
                onSendReleaseCode={handleSendReleaseCode}
                onVerifyReleaseCode={handleVerifyReleaseCode}
                maskedPhone={maskPhone(formData.phone || '')}
                smsCodeCountdown={smsCodeCountdown}
                smsCodeSending={smsCodeSending}
                smsCode={formData.smsCode}
                onSmsCodeChange={(code) => setFormField('smsCode', code)}
                onSendUpgradeSmsCode={handleSendUpgradeSmsCode}
                smsCodeError={errors.smsCode}
              />
            )}

            {currentStep === 4 && (
              <Step4Password
                formData={formData}
                errors={errors}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                passwordStrength={passwordStrength}
                agreed={agreed}
                setAgreed={setAgreed}
                setShowPassword={setShowPassword}
                setShowConfirmPassword={setShowConfirmPassword}
                setFormField={setFormField}
                validateField={validateField}
              />
            )}

            {currentStep === 5 && (
              <Step5Success
                result={registerSuccessData}
                formData={formData}
                onHome={() => {
                  handleClose();
                  navigate('/');
                }}
                onLogin={async () => {
                  handleClose();
                  // 会话仍有效时直接跳 /login 会被登录页的已登录守卫弹回首页，
                  // 先登出确保用户可重新登录其他账号
                  await signOut();
                  navigate('/login');
                }}
              />
            )}
          </div>

          {/* 底部按钮栏 */}
          <ModalFooter
            currentStep={currentStep}
            agreed={agreed}
            hasReadToBottom={hasReadToBottom}
            registerCountdown={registerCountdown}
            identityVerification={identityVerification}
            step3CanContinue={step3CanContinue}
            systemCheckResult={systemCheckResult}
            releaseToken={releaseFlow.token}
            smsCode={formData.smsCode}
            submitting={submitting}
            onPrev={prevStep}
            onNext={handleNext}
            onContinue={nextStep}
          />
        </div>
      </div>

      {/* 人工申请弹窗（mismatch 来源取消填写时返回「非本人信息」处理弹窗） */}
      {showManualApplication && (
        <ManualApplicationModal
          isOpen={showManualApplication}
          onClose={() => {
            setShowManualApplication(false);
            if (manualReviewSourceRef.current === 'mismatch' && matchedCustomer) {
              manualReviewSourceRef.current = 'normal';
              setShowCustomerBinding(true);
              setShowMismatchResolution(true);
            }
          }}
          formData={formData}
          reason={manualApplicationReason}
          setReason={setManualApplicationReason}
          photoUrl={manualApplicationPhoto}
          onPhotoChange={setManualApplicationPhoto}
          onSubmit={handleSubmitManualApplication}
        />
      )}

      {/* 身份合并弹窗 */}
      {showIdentityMerge && systemCheckResult?.matchedEntities && (
        <IdentityMergeModal
          isOpen={showIdentityMerge}
          onClose={() => setShowIdentityMerge(false)}
          entities={systemCheckResult.matchedEntities}
          selectedId={selectedPrimaryIdentity}
          onSelect={setSelectedPrimaryIdentity}
          onMerge={handleMergeIdentities}
          onAppeal={handleMergeAppeal}
          primaryPhone={formData.phone}
          formData={formData}
        />
      )}

      {/* 客户绑定弹窗（命中档案 → 确认识领 / 非本人信息） */}
      {showCustomerBinding && matchedCustomer && (
        <CustomerBindingModal
          isOpen={showCustomerBinding}
          onMismatch={() => {
            setShowCustomerBinding(false);
            setShowMismatchResolution(true);
          }}
          customer={matchedCustomer}
          formData={formData}
          onConfirm={handleConfirmCustomerBinding}
        />
      )}

      {/* 「非本人信息」处理弹窗（A.短信验证申诉 / B.人工审核） */}
      {showMismatchResolution && matchedCustomer && (
        <MismatchResolutionModal
          isOpen={showMismatchResolution}
          customer={matchedCustomer}
          formData={formData}
          onBack={() => {
            setShowMismatchResolution(false);
            setShowCustomerBinding(true);
          }}
          onManualReview={() => {
            manualReviewSourceRef.current = 'mismatch';
            setShowMismatchResolution(false);
            setManualApplicationReason(
              `认领核对：系统档案「${matchedCustomer.fullName}」与本人信息不符，申请人工审核档案归属`
            );
            setShowManualApplication(true);
          }}
          onResolved={(token) => {
            // 申诉验证通过：仅持有凭证（原档案未动），直接进入密码步完成注册
            setShowMismatchResolution(false);
            setDisputeToken(token);
            setStep(4);
            showToast('验证通过，请设置密码完成注册', 'success');
          }}
          onNotify={showToast}
        />
      )}
    </>,
    document.body
  );
}